import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from 'react-hot-toast';
import { LAYOUT_HEIGHT } from 'state/constants';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Divider,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { 
  FlightTakeoff, 
  FlightLand,
  AccessTime,
} from "@mui/icons-material";
import { EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable} from 'state/helpers';
import DataGridComponent from "components/DataGridComponent";


function getDelayLevel(min) {
  if (min < 15) return { label: "On Time",     color: "success" };
  if (min < 60) return { label: "Minor Delay", color: "warning" };
  return { label: "Major Delay", color: "error"   };
}

function getTurnaroundLevel(min) {
  if (min < 30)  return { label: "Very Short", color: "error",   advice: "Very tight turnaround - high risk of cascading delays." };
  if (min < 60)  return { label: "Short",      color: "warning", advice: "Tight window - ensure crew and ground handlers are ready." };
  if (min < 120) return { label: "Standard",   color: "success", advice: "Normal turnaround window for most aircraft types." };
  return { label: "Extended",   color: "info",    advice: "Longer than usual - check for technical or commercial holds." };
}

function parseTime(str) {
  if (!str || typeof str !== "string") return { hour: 0, minute: 0 };
  const [h, m] = str.split(":").map(Number);
  return { hour: isNaN(h) ? 0 : h, minute: isNaN(m) ? 0 : m };
}

const columns = [
  { field: "flightNo",   headerName: "FlightNo", flex: 1 },
  { field: "flightDate", headerName: "Date",     flex: 1.5,
    valueFormatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
  { field: "sector",     headerName: "Sector",   flex: 1.5, align: "center", headerAlign: "center" },
  { field: "acType",     headerName: "ACType",   flex: 1 },
  { field: "flightReg",  headerName: "Reg",      flex: 1.2 },
  { field: "std",        headerName: "Std",      flex: 1, align: "center", headerAlign: "center" },
  { field: "sta",        headerName: "Sta",      flex: 1, align: "center", headerAlign: "center" },
  { field: "etd",        headerName: "Etd",      flex: 1, align: "center", headerAlign: "center" },
  { field: "eta",        headerName: "Eta",      flex: 1, align: "center", headerAlign: "center" },
  { field: "atd",        headerName: "Atd",      flex: 1, align: "center", headerAlign: "center" },
  { field: "ata",        headerName: "Ata",      flex: 1, align: "center", headerAlign: "center" },
  { field: "tat",        headerName: "TAT",      flex: 1, align: "center", headerAlign: "center" },
];


function FlightCardItem({ flight, selected, onClick, theme }) {
  return (
    <Paper
      elevation={0}
      onClick={() => onClick(flight)}
      sx={{
        border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: selected ? `${theme.palette.primary.main}14` : "transparent",
        transition: "border-color 0.15s, background-color 0.15s",
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={flight.flightNo || "-"} color={selected ? "primary" : "default"} />
            <Typography variant="body2" fontWeight={600}>
              {flight.sector || `${flight.dep} → ${flight.arr}`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {flight.flightDate ? new Date(flight.flightDate).toLocaleDateString("en-GB") : ""}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            STD <b>{flight.std || "-"}</b>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            STA <b>{flight.sta || "-"}</b>
          </Typography>
          {flight.acType && (
            <Typography variant="caption" color="text.secondary">{flight.acType}</Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function DelayCard({ icon, label, minutes, theme }) {
  const level = getDelayLevel(minutes);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        flex: 1, 
        p: 1.5,
        border: `1px solid ${theme.palette[level.color].main}`, 
        borderRadius: 2, 
        textAlign: "center" 
      }}
    >
      <Stack spacing={0.5} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            {label}
          </Typography>
          <Chip 
            size="small" 
            label={level.label} 
            color={level.color} 
            sx={{ fontWeight: 600 }} 
          />
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="baseline">
          <Typography fontWeight={600} color={`${level.color}.main`}
            sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem" }, lineHeight: 1 }}
          >
            {minutes}
          </Typography>
          <Typography variant="caption" color="text.secondary">min</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function TurnaroundCard({ minutes, airport, theme }) {
  const level = getTurnaroundLevel(minutes);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1.5, 
        border: `1px solid ${theme.palette[level.color].main}`, 
        borderRadius: 2 
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccessTime color={level.color} />
          <Stack direction="row" spacing={0.5} alignItems="baseline">
            <Typography fontWeight={600} color={`${level.color}.main`}
              sx={{ fontSize: "1.6rem", lineHeight: 1 }}
            >
              {minutes}
            </Typography>
            <Typography variant="caption" color="text.secondary">min</Typography>
          </Stack>
          <Chip size="small" label={level.label} color={level.color} sx={{ fontWeight: 600 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary">{level.advice}</Typography>
      </Stack>
    </Paper>
  );
}


const PredictPanel = ({
  predictDelay, 
  predictTurnaround,
  isDelayReady, 
  isTurnaroundReady,
  initialFlight,
}) => {
  const theme       = useTheme();
  const isMobile    = useMediaQuery("(max-width: 600px)");
  const flightsData = useSelector((state) => state.flights);

  const [selectedFlight, setSelectedFlight] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [result, setResult] = useState(null);
  const [flights, setFlights] = useState([]); 

  useEffect(() => {
    if (initialFlight) setSelectedFlight(initialFlight);
  }, [initialFlight]);

  useEffect(() => {        
    const processFlights = () => {
      try {
        const flights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);
        setFlights(flights);
      } catch (error) {
        toast.error(error?.message || "Failed to process flights!");
      } 
    };

    processFlights();        
  }, [flightsData]);

  const handleRowClick = (params) => {
    setSelectedFlight(params.row);
    setResult(null);
  };

  const handlePredict = () => {
    if (!selectedFlight) return;
    const stdTime   = parseTime(selectedFlight.std);
    const staTime   = parseTime(selectedFlight.sta);
    const d         = new Date(selectedFlight.flightDate);
    const dayOfWeek = isNaN(d) ? 0 : d.getDay();
    const month     = isNaN(d) ? 0 : d.getMonth();

    const delayResult = isDelayReady ? predictDelay({
      dep:       selectedFlight.dep    || "",
      arr:       selectedFlight.arr    || "",
      acType:    selectedFlight.acType || "",
      dayOfWeek, month,
      stdHour:   stdTime.hour,
      stdMinute: stdTime.minute,
    }) : null;

    const turnaroundMinutes = isTurnaroundReady ? predictTurnaround({
      airport:   selectedFlight.arr    || "",
      acType:    selectedFlight.acType || "",
      ataHour:   staTime.hour,
      ataMinute: staTime.minute,
      dayOfWeek, month,
    }) : null;

    setResult({ delay: delayResult, turnaround: turnaroundMinutes });
  };


  return (
    <Box width="100%">
      {/* Flight list - always fills full viewport height */}
      <Box sx={{ height: LAYOUT_HEIGHT }}>
        {flights.length === 0 ? (
          <Alert severity="info">No flights loaded.</Alert>
        ) : isMobile ? (
          <Box sx={{ height: "100%", overflowY: "auto", pr: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Tap a flight to select
            </Typography>
            <Stack spacing={1}>
              {flights.map((flight) => (
                <FlightCardItem
                  key={flight._id}
                  flight={flight}
                  selected={selectedFlight?._id === flight._id}
                  onClick={(f) => { setSelectedFlight(f); setResult(null); }}
                  theme={theme}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <DataGridComponent
            rows={flights}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            props={{
              onRowClick: handleRowClick,
              getRowClassName: (params) =>
                params.id === selectedFlight?._id ? "Mui-selected" : "",
              density: "compact",
              sx: { cursor: "pointer" },
            }}
          />
        )}
      </Box>

      {/* Selected flight + results - flows below DataGrid, outer container scrolls */}
      {selectedFlight && (
        <Box
          sx={{
            mt: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 1,
            border: `1px solid ${theme.palette.primary.main}`,
          }}
        >
          {/* Flight info row + Predict button */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={selectedFlight.flightNo || "-"} color="primary" />
              <Chip size="small" label={`${selectedFlight.dep} → ${selectedFlight.arr}`} />
              <Chip variant="outlined" size="small" label={selectedFlight.acType || "N/A"} />
              {selectedFlight.flightDate && (
                <Chip
                  variant="outlined"
                  size="small"
                  label={`${selectedFlight.std || "--"} – ${selectedFlight.sta || "--"}  ${new Date(selectedFlight.flightDate).toLocaleDateString("en-GB")}`}
                />
              )}
            </Stack>
            <Button
              variant="outlined"
              size="medium"
              onClick={handlePredict}
              disabled={!isDelayReady && !isTurnaroundReady}
              sx={{ whiteSpace: "nowrap", flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
            >
              Predict
            </Button>
          </Stack>

          {/* Results */}
          {result && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={2}>
                {result.delay && (
                  <Box>
                    <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                      Delay Prediction
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <DelayCard
                        icon={<FlightTakeoff fontSize="small" color="action" />}
                        label="Departure Delay"
                        minutes={result.delay.depDelay}
                        theme={theme}
                      />
                      <DelayCard
                        icon={<FlightLand fontSize="small" color="action" />}
                        label="Arrival Delay"
                        minutes={result.delay.arrDelay}
                        theme={theme}
                      />
                    </Stack>
                  </Box>
                )}

                {result.turnaround !== null && result.turnaround !== undefined && (
                  <Box>
                    <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                      Turnaround at {selectedFlight.arr}
                    </Typography>
                    <TurnaroundCard
                      minutes={result.turnaround}
                      airport={selectedFlight.arr}
                      theme={theme}
                    />
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PredictPanel;
